import type { RouteObject } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import { IntegrationLogsPage } from "@/modules/integration-logs/pages/IntegrationLogsPage";
import type {
  IntegrationLogDirection,
  IntegrationLogStatus,
} from "@/lib/api/types";
import { useLocale } from "@admin/app/locale";

const STATUS_VALUES: IntegrationLogStatus[] = ["success", "failed", "pending"];
const DIRECTION_VALUES: IntegrationLogDirection[] = ["outbound", "inbound"];

function toNumber(value: string | null, fallback: number) {
  const parsed = value ? Number(value) : Number.NaN;
  return Number.isFinite(parsed) ? parsed : fallback;
}

function toText(value: string | null): string | undefined {
  return value?.trim() || undefined;
}

function toStatus(value: string | null): IntegrationLogStatus | undefined {
  const parsed = toText(value);
  return parsed && (STATUS_VALUES as string[]).includes(parsed)
    ? (parsed as IntegrationLogStatus)
    : undefined;
}

function toDirection(value: string | null): IntegrationLogDirection | undefined {
  const parsed = toText(value);
  return parsed && (DIRECTION_VALUES as string[]).includes(parsed)
    ? (parsed as IntegrationLogDirection)
    : undefined;
}

function AdminIntegrationLogsRoute() {
  const locale = useLocale();
  const [searchParams] = useSearchParams();

  return (
    <IntegrationLogsPage
      locale={locale}
      limit={toNumber(searchParams.get("limit"), 20)}
      offset={toNumber(searchParams.get("offset"), 0)}
      search={toText(searchParams.get("search"))}
      storeId={toText(searchParams.get("storeId"))}
      status={toStatus(searchParams.get("status"))}
      direction={toDirection(searchParams.get("direction"))}
      provider={toText(searchParams.get("provider"))}
      dateFrom={toText(searchParams.get("dateFrom"))}
      dateTo={toText(searchParams.get("dateTo"))}
    />
  );
}

export const integrationLogRoutes: RouteObject[] = [
  {
    path: "integration-logs",
    element: <AdminIntegrationLogsRoute />,
  },
];
